<script setup lang="ts">
import { ref } from 'vue';
import GaiaCard from '../base/GaiaCard.vue';
import GaiaCountPill from '../base/GaiaCountPill.vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaShadow } from '../themes/tokens';

const props = defineProps({
  /** Header line — what the panel is doing ("Zipping 2 folders…"). */
  title: {
    type: String,
    required: true,
  },
  /** Count pill beside the title — e.g. how many jobs are still running. */
  count: {
    type: Number,
    default: undefined,
  },
  /** The chevron; off for a panel whose body is a single line. */
  collapsible: {
    type: Boolean,
    default: true,
  },
  /** Body scroll cap, so a burst of jobs scrolls instead of growing a tower. */
  maxBodyHeight: {
    type: String,
    default: '260px',
  },
});

const collapsed = ref(false);

const panelShadow = gaiaShadow.panel;
</script>

<template>
  <!-- The floating background-task panel (bottom-right corner, inside
       GaiaCornerStack): downloads, demo seed. One card chrome for all of
       them, so simultaneous panels read as one system. -->
  <div class="gaia-task-panel">
    <gaia-card size="small">
      <template #header>
        <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
          <gaia-text truncate size="control" weight="semibold" tone="ink">
            {{ props.title }}
          </gaia-text>
          <gaia-count-pill v-if="props.count !== undefined && props.count > 0" active>
            {{ props.count }}
          </gaia-count-pill>
        </n-flex>
      </template>
      <template #header-extra>
        <n-flex align="center" :size="2" :wrap="false">
          <slot name="header-extra" />
          <gaia-icon-button
            v-if="props.collapsible"
            :dimension="24"
            quaternary
            :title="collapsed ? 'Expand' : 'Collapse'"
            @click="collapsed = !collapsed"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path v-if="collapsed" d="M4.5 9.5 8 6l3.5 3.5" />
              <path v-else d="M4.5 6.5 8 10l3.5-3.5" />
            </svg>
          </gaia-icon-button>
        </n-flex>
      </template>

      <n-collapse-transition :show="!collapsed">
        <n-scrollbar :style="{ maxHeight: props.maxBodyHeight }">
          <n-flex vertical :size="12">
            <slot />
          </n-flex>
        </n-scrollbar>
      </n-collapse-transition>
    </gaia-card>
  </div>
</template>

<style scoped>
/* Sizing and the floating-panel shadow only — the fixed bottom-right pinning
   lives on GaiaCornerStack. */
.gaia-task-panel {
  width: min(360px, calc(100vw - 24px));
  border-radius: 10px;
  box-shadow: v-bind(panelShadow);
}

/* The shadow's radius must match the card's own corner. */
.gaia-task-panel :deep(.n-card) {
  border-radius: 10px;
}
</style>
